/**
 * SocialInboxPage — Unified inbox for DMs + comments across Instagram/Facebook/TikTok/LinkedIn.
 * Currently mock-mode until Meta/TikTok/LinkedIn OAuth tokens are hinterlegt.
 * Features: company filter, platform filter, status badges, AI-suggestion, reply, ignore.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Inbox, Sparkles, Send, XCircle, Loader2, MessageCircle, AtSign, Instagram, Facebook, Linkedin, AlertTriangle, ShieldAlert, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PageHelp from "@/components/PageHelp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_META = {
  instagram: { icon: Instagram, color: "#E4405F", label: "Instagram" },
  facebook: { icon: Facebook, color: "#1877F2", label: "Facebook" },
  tiktok: { icon: AtSign, color: "#000000", label: "TikTok" },
  linkedin: { icon: Linkedin, color: "#0A66C2", label: "LinkedIn" },
};

const STATUS_STYLES = {
  new: { label: "Neu", cls: "bg-amber-100 text-amber-800" },
  ai_suggested: { label: "KI-Vorschlag", cls: "bg-blue-100 text-blue-800" },
  replied: { label: "Beantwortet", cls: "bg-emerald-100 text-emerald-800" },
  ignored: { label: "Ignoriert", cls: "bg-gray-200 text-gray-700" },
  escalated: { label: "Eskaliert", cls: "bg-red-100 text-red-800" },
};

export default function SocialInboxPage() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [manualOpen, setManualOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => setCompanies(r.data || []));
  }, []);

  const load = () => {
    setLoading(true);
    const params = {};
    if (companyId !== "all") params.company_id = companyId;
    if (platform !== "all") params.platform = platform;
    if (status !== "all") params.status = status;
    axios.get(`${API}/inbox`, { params, withCredentials: true })
      .then((r) => { setItems(r.data?.items || []); setCounts(r.data?.counts || {}); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Laden fehlgeschlagen"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [companyId, platform, status]);

  const seedDemo = async () => {
    if (companyId === "all") {
      toast.info("Bitte zuerst eine Firma auswaehlen");
      return;
    }
    try {
      await axios.post(`${API}/inbox/seed-demo`, { company_id: companyId }, { withCredentials: true });
      toast.success("5 Demo-Nachrichten eingefuegt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehlgeschlagen");
    }
  };

  return (
    <div data-testid="social-inbox-page" className="space-y-6 max-w-5xl">
      <PageHelp title="Wie funktioniert die Social-Inbox?" storageKey="social-inbox">
        <p>
          Hier landen <b>DMs und Kommentare</b> aller mit dem Studio verknuepften Social-Media-Konten —
          zusammengefasst pro Firma, mit KI-generierten Antwort-Vorschlaegen im Brand-Voice der Firma.
        </p>
        <h4>Zwei Quellen fuer Nachrichten</h4>
        <ol>
          <li><b>Automatisch</b> ueber Meta/TikTok/LinkedIn-Webhooks, sobald die OAuth-Verbindung steht (siehe „Social Media"-Seite).</li>
          <li><b>Manuell eingefuegt</b> via Button „Nachricht manuell hinzufuegen" — falls die offizielle API-Freigabe von Meta noch laeuft, kannst du DMs einfach reinkopieren und sofort einen KI-Vorschlag generieren lassen.</li>
        </ol>
        <h4>Status pro Nachricht</h4>
        <ul>
          <li><b>Neu</b> — noch nicht angefasst</li>
          <li><b>KI-Vorschlag</b> — Antwort wurde vorgeschlagen, wartet auf deine Freigabe</li>
          <li><b>Eskaliert</b> — KI hat erkannt, dass ein Mensch ran muss (Beschwerde, Frage zu Preis, etc.) — du bekommst eine E-Mail</li>
          <li><b>Beantwortet</b> — Antwort wurde an den Absender geschickt</li>
          <li><b>Ignoriert</b> — bewusst nicht beantwortet (z. B. Spam)</li>
        </ul>
        <h4>Auto-Antwort-Regeln pro Firma</h4>
        <p>
          In der Firmen-Detail-Seite kannst du pro Firma festlegen, welche Themen die KI automatisch beantworten darf
          (z. B. Oeffnungszeiten, Preise) und wann sie eskalieren soll (z. B. bei Beschwerden, Stornos, juristischen Fragen).
        </p>
      </PageHelp>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight flex items-center gap-3">
            <Inbox className="w-8 h-8 text-[#2563EB]" /> Social-Inbox
          </h1>
          <p className="text-[var(--studio-text-muted)] mt-1">
            DMs &amp; Kommentare aller verbundenen Kanaele — zentral mit KI-Vorschlaegen.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => setManualOpen(true)}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            data-testid="open-manual-inbox-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Nachricht manuell hinzufuegen
          </Button>
          <Button variant="outline" size="sm" onClick={seedDemo} data-testid="seed-demo-btn">
            Demo-Daten einfuegen
          </Button>
        </div>
      </div>

      <Card className="border-amber-300 bg-amber-50/50 shadow-none">
        <CardContent className="py-3 px-4 flex items-start gap-2 text-xs text-amber-900">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Mock-Modus aktiv</p>
            <p>Live-DMs + Kommentare werden automatisch eintreffen, sobald Meta / TikTok / LinkedIn-Tokens hinterlegt sind. Die KI-Antwort-Logik laeuft bereits jetzt im Mock-Modus.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-[var(--studio-border)] shadow-none">
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger className="w-48 h-9 text-sm" data-testid="inbox-company-filter">
              <SelectValue placeholder="Firma" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Firmen</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger className="w-36 h-9 text-sm" data-testid="inbox-platform-filter">
              <SelectValue placeholder="Plattform" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Plattformen</SelectItem>
              <SelectItem value="instagram">Instagram</SelectItem>
              <SelectItem value="facebook">Facebook</SelectItem>
              <SelectItem value="tiktok">TikTok</SelectItem>
              <SelectItem value="linkedin">LinkedIn</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 h-9 text-sm" data-testid="inbox-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="new">Neu ({counts.new || 0})</SelectItem>
              <SelectItem value="ai_suggested">KI-Vorschlag ({counts.ai_suggested || 0})</SelectItem>
              <SelectItem value="escalated">Eskaliert ({counts.escalated || 0})</SelectItem>
              <SelectItem value="replied">Beantwortet ({counts.replied || 0})</SelectItem>
              <SelectItem value="ignored">Ignoriert ({counts.ignored || 0})</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-[var(--studio-text-muted)]" />
      ) : items.length === 0 ? (
        <Card className="border-dashed border-[var(--studio-border)] shadow-none">
          <CardContent className="py-12 text-center text-[var(--studio-text-muted)]">
            <MessageCircle className="w-8 h-8 mx-auto opacity-40 mb-2" />
            <p className="text-sm">Keine Nachrichten in diesem Filter.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="inbox-list">
          {items.map((m) => <InboxItem key={m.id} msg={m} onAction={load} />)}
        </div>
      )}

      <ManualInboxDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        companies={companies}
        defaultCompanyId={companyId !== "all" ? companyId : (companies[0]?.id || "")}
        onCreated={() => { setManualOpen(false); load(); }}
      />
    </div>
  );
}

function ManualInboxDialog({ open, onOpenChange, companies, defaultCompanyId, onCreated }) {
  const [companyId, setCompanyId] = useState(defaultCompanyId || "");
  const [platform, setPlatform] = useState("instagram");
  const [type, setType] = useState("dm");
  const [sender, setSender] = useState("");
  const [content, setContent] = useState("");
  const [suggestNow, setSuggestNow] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setCompanyId(defaultCompanyId || "");
  }, [open, defaultCompanyId]);

  const reset = () => {
    setSender(""); setContent(""); setType("dm"); setPlatform("instagram"); setSuggestNow(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!companyId) { toast.info("Bitte Firma waehlen"); return; }
    if (!content.trim()) { toast.info("Bitte Nachricht eingeben"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/inbox/manual`,
        {
          company_id: companyId,
          platform,
          type,
          sender: sender.trim(),
          content: content.trim(),
          suggest_now: suggestNow,
        },
        { withCredentials: true }
      );
      toast.success(
        res.data?.ai_suggestion
          ? "Nachricht hinzugefuegt — KI-Vorschlag erstellt"
          : "Nachricht hinzugefuegt"
      );
      reset();
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehlgeschlagen");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="manual-inbox-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#2563EB]" /> Nachricht manuell einfuegen
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[var(--studio-text-muted)] -mt-1">
          Falls Meta/TikTok-API-Freigabe noch laeuft: DMs oder Kommentare hier hineinkopieren — die KI erstellt sofort einen Antwort-Vorschlag im Brand-Voice.
        </p>
        <form onSubmit={submit} className="space-y-3 mt-3">
          <div>
            <label className="text-xs text-[var(--studio-text-muted)]">Firma *</label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="mt-1" data-testid="manual-company-select">
                <SelectValue placeholder="Firma waehlen" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-[var(--studio-text-muted)]">Plattform</label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger className="mt-1" data-testid="manual-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="tiktok">TikTok</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[var(--studio-text-muted)]">Typ</label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-1" data-testid="manual-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dm">Direktnachricht (DM)</SelectItem>
                  <SelectItem value="comment">Kommentar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs text-[var(--studio-text-muted)]">Absender</label>
            <Input
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              placeholder="@username oder Name"
              className="mt-1"
              data-testid="manual-sender-input"
            />
          </div>
          <div>
            <label className="text-xs text-[var(--studio-text-muted)]">Nachricht *</label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Kompletter Inhalt der DM oder des Kommentars …"
              rows={4}
              required
              className="mt-1"
              data-testid="manual-content-input"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-[var(--studio-text-muted)] cursor-pointer">
            <input
              type="checkbox"
              checked={suggestNow}
              onChange={(e) => setSuggestNow(e.target.checked)}
              data-testid="manual-suggest-now-checkbox"
            />
            <Sparkles className="w-3 h-3 text-[#2563EB]" />
            KI-Antwort sofort vorschlagen lassen
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={submitting || !content.trim() || !companyId}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="manual-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Hinzufuegen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InboxItem({ msg, onAction }) {
  const [loading, setLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [showReply, setShowReply] = useState(false);
  const PM = PLATFORM_META[msg.platform] || {};
  const Icon = PM.icon || MessageCircle;
  const st = STATUS_STYLES[msg.status] || STATUS_STYLES.new;

  const suggest = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/inbox/${msg.id}/suggest-reply`, {}, { withCredentials: true });
      setReplyText(r.data?.suggestion?.reply || "");
      setShowReply(true);
      toast.success(r.data?.status === "escalated" ? "KI empfiehlt Eskalation" : "KI-Vorschlag erstellt");
      onAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "KI-Fehler");
    }
    setLoading(false);
  };

  const send = async () => {
    if (!replyText.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/inbox/${msg.id}/reply`, { text: replyText }, { withCredentials: true });
      toast.success("Antwort gesendet (Mock)");
      setShowReply(false);
      onAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
    setLoading(false);
  };

  const ignore = async () => {
    try {
      await axios.post(`${API}/inbox/${msg.id}/mark-ignored`, {}, { withCredentials: true });
      toast.success("Ignoriert");
      onAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <Card className="border-[var(--studio-border)] shadow-none" data-testid={`inbox-item-${msg.id}`}>
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
               style={{ backgroundColor: (PM.color || "#888") + "22", color: PM.color || "#555" }}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold">{msg.sender}</p>
              <Badge variant="outline" className="text-[9px]">{PM.label || msg.platform} · {msg.type === "dm" ? "DM" : "Kommentar"}</Badge>
              <Badge className={`text-[9px] border-0 ${st.cls}`}>{st.label}</Badge>
              <span className="text-[10px] text-[var(--studio-text-muted)]">
                {msg.created_at ? new Date(msg.created_at).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
              </span>
            </div>
            <p className="text-sm mt-1">{msg.content}</p>

            {msg.ai_suggested_reply?.reply && (
              <div className="mt-2 p-2 rounded-md bg-[#2563EB]/5 border border-[#2563EB]/20">
                <div className="flex items-center gap-1 text-[10px] text-[#2563EB] font-medium mb-0.5">
                  <Sparkles className="w-3 h-3" /> KI-Vorschlag · Confidence {Math.round((msg.ai_suggested_reply.confidence || 0) * 100)}%
                  {msg.ai_suggested_reply.escalate && (
                    <span className="ml-1 text-red-600 flex items-center gap-0.5"><ShieldAlert className="w-3 h-3" /> Eskalation empfohlen</span>
                  )}
                </div>
                <p className="text-xs">{msg.ai_suggested_reply.reply}</p>
              </div>
            )}

            {msg.human_reply?.text && (
              <div className="mt-2 p-2 rounded-md bg-emerald-50 border border-emerald-200">
                <p className="text-[10px] text-emerald-800 font-medium">
                  Antwort gesendet {msg.human_reply.sent_mode === "mock" ? "(Mock)" : "(Live)"} · {new Date(msg.human_reply.sent_at).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
                <p className="text-xs mt-0.5">{msg.human_reply.text}</p>
              </div>
            )}

            {showReply && msg.status !== "replied" && msg.status !== "ignored" && (
              <div className="mt-2 space-y-2">
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={2}
                  placeholder="Antwort eingeben..."
                  className="text-sm"
                  data-testid={`reply-text-${msg.id}`}
                />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setShowReply(false)} className="h-7 text-xs">Abbrechen</Button>
                  <Button size="sm" onClick={send} disabled={loading || !replyText.trim()}
                          className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-7 text-xs"
                          data-testid={`send-reply-${msg.id}`}>
                    <Send className="w-3 h-3 mr-1" /> Senden
                  </Button>
                </div>
              </div>
            )}

            {!showReply && msg.status !== "replied" && msg.status !== "ignored" && (
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={suggest} disabled={loading} data-testid={`suggest-${msg.id}`}>
                  {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  KI-Vorschlag
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => { setReplyText(msg.ai_suggested_reply?.reply || ""); setShowReply(true); }}
                        data-testid={`manual-reply-${msg.id}`}>
                  <Send className="w-3 h-3 mr-1" /> Antworten
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-[var(--studio-text-muted)]"
                        onClick={ignore} data-testid={`ignore-${msg.id}`}>
                  <XCircle className="w-3 h-3 mr-1" /> Ignorieren
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
